import React from 'react';
import { motion } from 'framer-motion';
import { 
  PhoneCall, LayoutDashboard, Database, UserCheck, Calendar, Lock, 
  Activity, ShieldCheck, ArrowRight, ChefHat, Smartphone, Receipt, 
  Stethoscope, Utensils, BarChart3, ScanLine, Wifi, Mic, Box, FileText, Zap
} from 'lucide-react';

const Services = () => {
  const brandGold = "#ffb700";

  // 1. VISUAL: RESTAURANT (Holographic Stack)
  const HoloLayer = ({ icon: Icon, label, color, yOffset, delay }) => (
    <motion.div
      initial={{ y: yOffset, opacity: 0 }}
      whileInView={{ y: [yOffset, yOffset - 10, yOffset], opacity: 1 }}
      transition={{ 
        y: { duration: 3, repeat: Infinity, ease: "easeInOut", delay: delay },
        opacity: { duration: 0.5 }
      }}
      className="absolute left-0 right-0 mx-auto flex flex-col items-center justify-center z-10"
      style={{ top: '30%' }} 
    >
      <div className={`relative w-16 h-16 md:w-20 md:h-20 bg-slate-900/80 backdrop-blur-md border border-${color}-500/50 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(255,183,0,0.15)] group hover:scale-110 transition-transform duration-300`}>
        <Icon size={28} className={`text-${color}-500 md:w-8 md:h-8`} />
        {/* Corner Accents */}
        <div className={`absolute -top-1 -left-1 w-3 h-3 border-t-2 border-l-2 border-${color}-500/50 rounded-tl-lg`} />
        <div className={`absolute -bottom-1 -right-1 w-3 h-3 border-b-2 border-r-2 border-${color}-500/50 rounded-br-lg`} />
      </div>
      <div className={`mt-2 md:mt-3 px-3 py-1 rounded-full bg-${color}-500/10 border border-${color}-500/20 text-${color}-500 text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em]`}>
        {label}
      </div>
    </motion.div>
  );

  // 2. VISUAL: MODEL HUB (Connected Nodes)
  const ModalHubVisual = () => {
    return (
      <div className="relative w-full max-w-lg h-[300px] md:h-[400px] flex items-center justify-center">
         
         {/* SVG Connection Lines */}
         <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 400">
            <defs>
              <linearGradient id="pulseGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(168, 85, 247, 0.1)" />
                <stop offset="50%" stopColor="rgba(168, 85, 247, 0.8)" />
                <stop offset="100%" stopColor="rgba(168, 85, 247, 0.1)" />
              </linearGradient>
            </defs>
            {/* Lines connecting center (200,200) to corners */}
            {[
              "M 200 200 L 100 100", // Top Left
              "M 200 200 L 300 100", // Top Right
              "M 200 200 L 100 300", // Bottom Left
              "M 200 200 L 300 300"  // Bottom Right
            ].map((path, i) => (
              <g key={i}>
                <path d={path} stroke="rgba(255,255,255,0.05)" strokeWidth="2" />
                <motion.path 
                   d={path} 
                   stroke="url(#pulseGrad)" 
                   strokeWidth="3"
                   strokeLinecap="round"
                   initial={{ pathLength: 0, opacity: 0, strokeDasharray: "0 1" }}
                   animate={{ pathLength: [0, 1, 0], opacity: [0, 1, 0], strokeDashoffset: [0, -20, 0] }}
                   transition={{ duration: 3, delay: i * 0.5, repeat: Infinity, ease: "easeInOut" }}
                />
              </g>
            ))}
         </svg>
  
         {/* Central Core */}
         <div className="relative z-10 w-20 h-20 md:w-28 md:h-28 bg-[#09090b] border border-purple-500/50 rounded-2xl md:rounded-3xl flex flex-col items-center justify-center shadow-[0_0_60px_rgba(168,85,247,0.2)] z-20">
            <div className="absolute inset-0 bg-purple-500/10 rounded-2xl md:rounded-3xl animate-pulse" />
            <Box size={32} className="text-purple-400 mb-1 md:mb-2 md:w-10 md:h-10" />
            <span className="text-[9px] md:text-[10px] font-black text-purple-400 uppercase tracking-widest">Core</span>
         </div>
  
         {/* Satellites */}
         <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute top-[10%] md:top-[15%] left-[10%] md:left-[15%] w-12 h-12 md:w-16 md:h-16 bg-slate-900/90 border border-indigo-500/30 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm z-20">
            <ShieldCheck size={20} className="text-indigo-400 md:w-6 md:h-6" />
            <div className="absolute -bottom-5 md:-bottom-6 text-[8px] md:text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Privacy</div>
         </motion.div>
  
         <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="absolute top-[10%] md:top-[15%] right-[10%] md:right-[15%] w-12 h-12 md:w-16 md:h-16 bg-slate-900/90 border border-pink-500/30 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm z-20">
            <FileText size={20} className="text-pink-400 md:w-6 md:h-6" />
            <div className="absolute -bottom-5 md:-bottom-6 text-[8px] md:text-[10px] font-bold text-pink-500 uppercase tracking-wider">Docs</div>
         </motion.div>
  
         <motion.div animate={{ y: [0, -18, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} className="absolute bottom-[10%] md:bottom-[15%] left-[10%] md:left-[15%] w-12 h-12 md:w-16 md:h-16 bg-slate-900/90 border border-cyan-500/30 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm z-20">
            <Lock size={20} className="text-cyan-400 md:w-6 md:h-6" />
            <div className="absolute -top-5 md:-top-6 text-[8px] md:text-[10px] font-bold text-cyan-500 uppercase tracking-wider">Secure</div>
         </motion.div>
  
         <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1.5 }} className="absolute bottom-[10%] md:bottom-[15%] right-[10%] md:right-[15%] w-12 h-12 md:w-16 md:h-16 bg-slate-900/90 border border-green-500/30 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm z-20">
            <Zap size={20} className="text-green-400 md:w-6 md:h-6" />
            <div className="absolute -top-5 md:-top-6 text-[8px] md:text-[10px] font-bold text-green-500 uppercase tracking-wider">Speed</div>
         </motion.div>
      </div>
    )
  }

  return (
    <div className="pt-24 md:pt-32 pb-20 px-4 md:px-12 bg-[#020408] overflow-hidden">
      
      {/* ==================== RESTAURANT SERVICE SECTION ==================== */}
      <section className="max-w-7xl mx-auto mb-20 md:mb-40 grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
        
        {/* LEFT: TEXT CONTENT */}
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#ffb700]/30 bg-[#ffb700]/5 text-[#ffb700] text-[10px] md:text-xs font-bold mb-6 md:mb-8 uppercase tracking-widest">
            <Utensils size={12} /> Restaurant AI
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 text-white leading-tight tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ffb700] to-orange-500">Restaurant Ai Solutions</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg mb-8 md:mb-10 leading-relaxed max-w-xl">
            The operating system for modern eateries. We replace chaotic front-desk operations with a Neural AI that handles voice orders, tracks inventory in real-time, and secures revenue with bank-grade encryption.
          </p>
          
          <div className="space-y-6 md:space-y-8 mb-8 md:mb-12">
            {[
              { 
                title: "Neural Voice Ordering", 
                desc: "Handles 100+ concurrent calls instantly. Zero wait times. Upsells sides and drinks automatically with human-like conversation.",
                icon: PhoneCall,
                color: "orange"
              },
              { 
                title: "Live Inventory & Analytics", 
                desc: "Track every gram of stock. Real-time dashboards show revenue pulses, peak hours, and top-selling items.",
                icon: BarChart3,
                color: "orange" 
              },
              { 
                title: "Bank-Grade Security", 
                desc: "Stripe-integrated payments with end-to-end encryption and EIN verification for secure transactions.",
                icon: ShieldCheck,
                color: "orange" 
              }
            ].map((item, i) => (
              <div key={i} className="flex gap-4 md:gap-5 group">
                <div className="mt-1 shrink-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-[#ffb700]/10 border border-[#ffb700]/20 flex items-center justify-center text-[#ffb700] group-hover:scale-110 transition-transform">
                    <item.icon size={20} className="md:w-6 md:h-6" />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">{item.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <a 
            href="https://starkxrestoservices.onrender.com/" 
            className="w-full md:w-auto inline-flex justify-center items-center gap-3 bg-[#ffb700] text-black px-8 py-4 rounded-xl font-black text-sm uppercase tracking-wider hover:bg-white transition-colors"
          >
            Know More <ArrowRight size={18} />
          </a>
        </motion.div>

        {/* RIGHT: THE ANTI-GRAVITY SERVICE TRAY */}
        <div className="relative h-[400px] md:h-[600px] flex items-center justify-center perspective-1000 mt-10 md:mt-0">
          <div className="relative w-64 md:w-80 h-full flex flex-col items-center justify-center">
            
            <div className="absolute bottom-4 md:bottom-10 w-48 md:w-64 h-16 md:h-24 bg-gradient-to-t from-slate-900 to-[#ffb700]/10 rounded-[100%] border border-[#ffb700]/30 shadow-[0_0_50px_rgba(255,183,0,0.2)] transform rotate-x-12">
               <div className="absolute inset-4 border border-[#ffb700]/20 rounded-[100%] animate-ping opacity-20" />
            </div>

            <div className="absolute bottom-10 md:bottom-20 w-32 md:w-40 h-[250px] md:h-[400px] bg-gradient-to-t from-[#ffb700]/20 to-transparent blur-xl pointer-events-none" />

            {/* Adjusted Offsets for Mobile & Desktop */}
            <div className="relative h-[300px] md:h-[400px] w-full">
               <HoloLayer icon={Smartphone} label="Voice AI" color="orange" yOffset={-100} delay={0} />
               <HoloLayer icon={Utensils} label="Restaurant" color="orange" yOffset={0} delay={1} />
               <HoloLayer icon={Receipt} label="Order" color="orange" yOffset={100} delay={2} />

               <motion.div 
                 animate={{ top: ["0%", "100%", "0%"], opacity: [0, 1, 0] }}
                 transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                 className="absolute left-1/2 -translate-x-1/2 w-32 md:w-48 h-1 bg-[#ffb700] shadow-[0_0_20px_#ffb700] z-20"
               />
               
               <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#ffb700]/30 to-transparent z-0" />
            </div>

          </div>
        </div>

      </section>


      {/* ==================== CLINIC SERVICE SECTION ==================== */}
      <section className="max-w-7xl mx-auto mb-20 md:mb-40 grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
        
        {/* TEXT CONTENT (Order 1 on mobile, Order 2 on desktop) */}
        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="order-1 lg:order-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 text-cyan-400 text-[10px] md:text-xs font-bold mb-6 md:mb-8 uppercase tracking-widest">
            <Activity size={12} /> Clinical Ai
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 text-white leading-tight tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">Healthcare AI</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg mb-8 md:mb-10 leading-relaxed max-w-xl">
            The central nervous system for modern healthcare. We bridge the gap between patient needs and provider schedules with a fully autonomous, HIPAA-compliant neural network.
          </p>
          
          <div className="space-y-6 md:space-y-8 mb-8 md:mb-12">
            {[
              { 
                title: "AI Receptionist & Triage", 
                desc: "A 24/7 voice agent that answers clinic calls, checks doctor availability, and books appointments directly into your schedule.",
                icon: PhoneCall,
                color: "cyan"
              },
              { 
                title: "EHR Synapse & Command Center", 
                desc: "Seamless bi-directional sync with Epic & Cerner. Track patient vitals and history in a unified, real-time dashboard.",
                icon: LayoutDashboard,
                color: "cyan"
              },
              { 
                title: "HIPAA Shield Protocol", 
                desc: "Military-grade encryption for every byte of patient data. Fully compliant and secure by design.",
                icon: Lock,
                color: "cyan"
              }
            ].map((item, i) => (
              <div key={i} className="flex gap-4 md:gap-5 group">
                <div className="mt-1 shrink-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 group-hover:scale-110 transition-transform">
                    <item.icon size={20} className="md:w-6 md:h-6" />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">{item.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <a 
            href="https://starkxaihealth.onrender.com" 
            className="w-full md:w-auto inline-flex justify-center items-center gap-3 bg-cyan-500 text-black px-8 py-4 rounded-xl font-black text-sm uppercase tracking-wider hover:bg-white transition-colors"
          >
            Know More <ArrowRight size={18} />
          </a>
        </motion.div>

        {/* CLINIC VISUAL (Order 2 on mobile, Order 1 on desktop) */}
        <div className="relative order-2 lg:order-1 h-[350px] md:h-[500px] flex items-center justify-center mt-10 md:mt-0">
          <div className="absolute inset-0 bg-cyan-500/10 blur-[60px] md:blur-[100px] rounded-full pointer-events-none" />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            whileInView={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.8 }}
            className="relative z-10 w-full max-w-[280px] md:max-w-md aspect-square"
          >
            <div className="relative w-full h-full border border-cyan-500/20 rounded-full flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
               <div className="absolute inset-0 border border-cyan-500/10 rounded-full animate-[spin_10s_linear_infinite]" />
               <div className="absolute inset-4 border border-cyan-500/10 rounded-full animate-[spin_15s_linear_infinite_reverse]" />
               
               <div className="w-24 h-24 md:w-32 md:h-32 bg-cyan-500/10 rounded-full border border-cyan-500/50 flex items-center justify-center shadow-[0_0_50px_rgba(6,182,212,0.2)]">
                  <Stethoscope size={40} className="text-cyan-400 md:w-12 md:h-12" />
               </div>

               <motion.div animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }} className="absolute inset-0">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 border border-cyan-500 text-cyan-500 p-2 md:p-3 rounded-lg md:rounded-xl shadow-lg">
                    <Lock size={16} className="md:w-5 md:h-5" />
                  </div>
               </motion.div>
               <motion.div animate={{ rotate: -360 }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }} className="absolute inset-0">
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-slate-900 border border-cyan-500 text-cyan-500 p-2 md:p-3 rounded-lg md:rounded-xl shadow-lg">
                    <Database size={16} className="md:w-5 md:h-5" />
                  </div>
               </motion.div>
            </div>
          </motion.div>
        </div>

      </section>

      {/* ==================== MODAL HUB SECTION ==================== */}
      <section className="max-w-7xl mx-auto mb-10 md:mb-20 border-t border-white/5 pt-16 md:pt-20">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 items-center">
          
          {/* TEXT CONTENT */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-purple-500/30 bg-purple-500/5 text-purple-400 text-[10px] md:text-xs font-bold mb-6 md:mb-8 uppercase tracking-widest">
              <Box size={12} /> Extension Ecosystem
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-4 md:mb-6 text-white tracking-tight">
              The <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-600">Model Hub</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg mb-8 md:mb-10 leading-relaxed max-w-xl">
              Enhance your existing infrastructure with modular, plug-and-play AI components. 
              Simple installation, instant compliance, and enterprise-grade reliability.
            </p>

            <div className="space-y-6 md:space-y-8 mb-8 md:mb-12">
              {[
                { 
                  title: "HIPAA Compliance Suite", 
                  desc: "Deploy Plug and play HIPAA Compliant models  with ultra low latency and high accuracy with total data isolation.",
                  icon: ShieldCheck,
                  color: "purple"
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 md:gap-5 group">
                  <div className="mt-1 shrink-0">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-${item.color}-500/10 border border-${item.color}-500/20 flex items-center justify-center text-${item.color}-400 group-hover:scale-110 transition-transform`}>
                      <item.icon size={20} className="md:w-6 md:h-6" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <a href="comming" className="w-full md:w-auto inline-block text-center bg-white text-black px-10 py-4 rounded-xl font-bold text-sm uppercase tracking-wider hover:bg-purple-500 hover:text-white transition-all shadow-lg hover:shadow-purple-500/30">
              Coming Soon..
            </a>
          </motion.div>

          {/* VISUAL */}
          <div className="flex justify-center lg:justify-end mt-10 md:mt-0">
             <ModalHubVisual />
          </div>

        </div>
      </section>

    </div>
  );
};

export default Services;